<template>
  <vx-card class="mt-base" :title="activityTitle">
    <p class="mb-5">
      Being a personal trainer often means meeting or chatting with potential clients in an initial
      consultation. This is to find out if your services are a good fit for the client and their
      needs before you get started.
    </p>
    <p class="mb-5">
      In order to convert a prospective client into a paying one, it’s a good idea to get organised,
      be professional and communicate well from the get-go.
    </p>

    <div class="vx-row my-5 flex items-center justify-center">
      <div class="vx-col xl:w-1/2 lg:w-1/2 w-full my-5">
        <p class="mb-5">
          The first thing you’ll need to do when taking on a new client is conduct a ‘New Client
          Screening’.
        </p>
        <p class="mb-5">
          This might initially seem time-consuming but it’s a necessary part of your onboarding
          procedure. It helps both you and the client in the long-run and will soon become second
          nature!
        </p>
        <p class="mb-5">
          Screening can be covered in your initial consultation by asking a series of questions or
          getting the client to fill out a questionnaire.
        </p>
        <p class="mb-5">
          It’s important to develop a clear, step-by-step process to go through with each new client
          to make your life easier and their experience smoother.
        </p>
      </div>
      <div class="vx-col xl:w-1/2 lg:w-1/2 w-full my-5">
        <img
          src="@/assets/images/experiences/fitness/consultation_image.jpg"
          alt=""
          class="mx-auto img-responsive"
        />
      </div>
    </div>
    <p class="mb-5">
      In the upcoming activities, you’ll take a closer look at things you can do to create a strong
      first impression with a prospective client.
    </p>
    <p class="mb-5">
      You’ll also learn effective ways of obtaining client information which will help you design a
      programme to meet their fitness goals.
    </p>
    <p class="mb-5">
      To find out more about the importance of an initial consultation and how to conduct it, watch
      the video below:
    </p>
    <responsive-youtube-wrapper :video="videos[1].url" :image="videos[1].image" />

    <h4 class="mb-5">Task 1:</h4>
    <p class="mb-5">
      Now you have a good idea of how to conduct an initial personal training consultation, you’ll
      need to find a ‘guinea pig’ to practise your client onboarding skill! This could be a friend
      or a family member - you could even conduct the practice session virtually.
    </p>
    <p class="mb-5">
      Before beginning the consultation, be sure to reassure your ‘prospective client’ that the
      answers they provide will be kept confidential, and that the information will only be used to
      help you to help them reach their goals.
    </p>
    <p class="mb-5">
      As you’ll have to upload the forms completed during these activities, if your volunteer client
      wishes to remain anonymous, just use a fictitious name when completing the forms.
    </p>
    <p class="mb-5 text-bold">The Consultation Form</p>
    <div class="vx-row my-5 flex items-center justify-center">
      <div class="vx-col xl:w-1/2 lg:w-1/2 w-full my-5">
        <p class="mb-5">The first step is to open the consultation form on the right:</p>
        <p class="mb-5">
          Now work your way through this form with your client, either in-person or virtually.
        </p>
        <p class="mb-5">
          Encourage your ‘prospective client’ to answer in detail as you go through the questions
          with them. You can take notes as they speak and write up the form afterward.
        </p>
        <p class="mb-5">
          Be sure to demonstrate good listening skills while your client speaks. For example:
        </p>
        <ul class="default-list">
          <li>Pay attention</li>
          <li>Look at the speaker</li>
          <li>Ask questions</li>
          <li>Avoid interrupting</li>
        </ul>
      </div>
      <div class="vx-col xl:w-1/2 lg:w-1/2 w-full my-5">
        <a :href="'content/fitness/pt_consultation_form.pdf' | blob_url" target="_blank">
          <img
            src="@/assets/images/experiences/fitness/initial_consult_form.png"
            alt="Open the personal training consultation form (PDF, opens in a new tab)"
            class="mx-auto img-responsive"
          />
        </a>
      </div>
    </div>

    <p class="mb-5 text-italic">
      When the form has been completed for your prospective client, you can upload it here:
    </p>
    <fileupload-button-immediate
      v-model="uploaded_files['uploaded_file_1']"
      :is-editable="isEditable"
      :upload-btn-text="'Upload Completed Consultation Form'"
      index="1"
      filetype="pdf"
    />

    <p class="mb-5 text-bold">The PAR_Q Form</p>
    <p class="mb-5">
      Often, a questionnaire is provided to make sure participants are of good physical health
      before starting an exercise programme.
    </p>
    <div class="vx-row my-5 flex items-center justify-center">
      <div class="vx-col xl:w-1/2 lg:w-1/2 w-full my-5">
        <p class="mb-5">
          The Physical Activity Readiness Questionnaire (PAR-Q) is a common method of uncovering
          health and lifestyle issues before starting an exercise programme.
        </p>
        <p class="mb-5">
          If the PAR-Q reveals an issue, it is advisable for the participant to seek a doctor's
          advice before starting an exercise programme.
        </p>
        <p class="mb-5">
          Now download the PAR-Q form template on the right and ask your volunteer client to
          complete it.
        </p>
        <p class="mb-5">
          You can send it to them via email or ask them the questions and complete it as they
          answer!
        </p>
      </div>
      <div class="vx-col xl:w-1/2 lg:w-1/2 w-full my-5">
        <a :href="'content/fitness/par_q_form.pdf' | blob_url" target="_blank">
          <img
            src="@/assets/images/experiences/fitness/par_q_form.png"
            alt="Open the PAR-Q Physical Activity Readiness Questionnaire form template (PDF, opens in a new tab)"
            class="mx-auto img-responsive"
          />
        </a>
      </div>
    </div>
    <p class="mb-5 text-italic">When finished, upload a copy here</p>
    <fileupload-button-immediate
      v-model="uploaded_files['uploaded_file_2']"
      :is-editable="isEditable"
      :upload-btn-text="'Upload Completed PAR-Q Form'"
      index="2"
      filetype="pdf"
    />

    <save-buttons :is-editable="isEditable" @save="save" />
  </vx-card>
</template>

<script>
import methods from '@/views/WorkExperiences/components/activity_js/methods';
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';

export default {
  data() {
    return {
      videos: {
        1: {
          image: require('@/assets/images/experiences/fitness/activity_1_video_splash.jpg'),
          url: 'https://www.youtube.com/embed/KcKZ1QCUij8',
        },
      },
      uploaded_files: {
        uploaded_file_1: '',
        uploaded_file_2: '',
      },
    };
  },
  methods: methods,
  computed: computed,
  inject: inject,
  mounted() {
    if (typeof this.uploaded_files !== 'undefined') {
      for (let i = 1; i <= Object.keys(this.uploaded_files).length; i++) {
        if (typeof this.data['uploaded_file_' + i] !== 'undefined') {
          this.uploaded_files['uploaded_file_' + i] = this.data['uploaded_file_' + i];
        }
      }
    }
  },
};
</script>
